import { useEffect } from "react";
import {
  BrowserRouter as Router,
  Navigate,
  Routes,
  Route,
  useLocation,
  useParams,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";

// Shared components
import Navbar from "./components/Shared/NavigationBar/Navbar";
import Footer from "./components/Shared/Footer/Footer";
import ScrollToTop from "./components/Shared/ScrollToTop";

// Landing pages
import HomePage from "./components/HomePage";
import JobSeekersPage from "./components/JobSeekers";

// Pages
import About from "./components/About/About";
import AmbassadorProgram from "./components/AmbassadorProgram/AmbassadorProgram";
import Accessibility from "./components/Accessibility/Accessibility";
import CareerCenter from "./components/CareerCenters";
import Pricing from "./components/Pricing";
import Insights from "./components/Insights";
import Article from "./components/Insights/Article";
import AlumniPage from "./components/Alumni";

import NewUserMobile from "./components/NewUserMobile/NewUserMobile";

import Waitlist from "./components/Waitlist/Waitlist";
import Newsletter from "./components/Newsletter/newsletter";
import NotFound from "./components/NotFound/NotFound";

import { REDIRECTS, REDIRECT_PREFIXES } from "./seo/site";

import "./styles.css";

/* /resources/foo -> /insights/foo. The splat holds everything after the old
   section root; with no splat this is the bare section and `to` already is
   the answer. */
const MovedSection = ({ to }) => {
  const rest = useParams()["*"];
  return <Navigate to={rest ? `${to}/${rest}` : to} replace />;
};

const ScrollHandler = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.state?.scrollTo) {
      const el = document.getElementById(location.state.scrollTo);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    }
  }, [location]);

  return (
    <>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/job-seekers" element={<JobSeekersPage />} />
        <Route path="/about" element={<About />} />
        <Route path="/ambassador-program" element={<AmbassadorProgram />} />
        <Route path="/career-centers" element={<CareerCenter />} />
        <Route path="/new-user-mobile.html" element={<NewUserMobile />} />
        <Route path="/waitlist" element={<Waitlist />} />
        <Route path="/newsletter" element={<Newsletter />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/insights" element={<Insights />} />
        <Route path="/insights/:slug" element={<Article />} />
        <Route path="/accessibility" element={<Accessibility />} />
        <Route path="/alumni" element={<AlumniPage />} />

        {/* Paths that have moved. The pair lives in src/seo/routes.json so
            the sitemap can list the destination and never the old URL. */}
        {REDIRECTS.map(({ from, to }) => (
          <Route key={from} path={from} element={<Navigate to={to} replace />} />
        ))}

        {/* Sections that have moved, with everything beneath them. The
            article URLs under /resources are the ones Google actually holds,
            so the old slug has to carry across, not just land on the index. */}
        {REDIRECT_PREFIXES.map(({ from, to }) => (
          <Route
            key={from}
            path={`${from}/*`}
            element={<MovedSection to={to} />}
          />
        ))}

        {/* Everything else. Without this an unknown path rendered nothing
            at all and still answered 200, so dead URLs looked like real
            pages to a crawler. */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
};

function App() {
  return (
    <HelmetProvider>
      <Router>
        <ScrollToTop />
        <Navbar />
        <ScrollHandler />
        <Footer />
      </Router>
    </HelmetProvider>
  );
}

export default App;
